import React, { useEffect, useState } from 'react';
import { useAuth } from '../components/AuthProvider';
import { Calendar, Trophy, ChevronRight, Medal, Users } from 'lucide-react';
import { Link, useSearchParams } from 'react-router-dom';
import { useApiJson } from '../hooks/useApi';
import type { BannerConfig, FeatureKey, QuizCampaignSummary } from '../types';
import { canAccessFeatureFromSnapshot, featureUnavailableMessage } from '../lib/feature-access';
import { cn } from '../lib/utils';
import { shouldShowCampaignSponsorBlock } from '../lib/sponsor';
import { CampaignSponsorBlock } from '../components/quiz/CampaignSponsorBlock';
import { CampaignSponsorLogo } from '../components/quiz/CampaignSponsorLogo';
import { BannerCountdown } from '../components/dashboard/BannerCountdown';
import { hasBannerCountdown } from '../lib/banner-countdown';
import { formatDateTime } from '../lib/format-datetime';
import { MarkdownContent } from '../components/markdown/MarkdownContent';

interface BannerDto {
  ok: boolean;
  banner: BannerConfig;
}

export function Dashboard() {
  const { member, featureAccess } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [unavailableMessage, setUnavailableMessage] = useState<string | null>(null);
  const { data: bannerData } = useApiJson<BannerDto>('/api/settings/dashboardBanner');
  const { data: summary } = useApiJson<QuizCampaignSummary>('/api/quiz-campaigns/summary');

  const banner = bannerData?.banner;
  const campaign = summary?.campaign;
  const showQuizCard =
    summary?.quizNavVisible && canAccessFeatureFromSnapshot(featureAccess, 'quiz');
  const showLeaderboardCard =
    summary?.leaderboardNavVisible && canAccessFeatureFromSnapshot(featureAccess, 'leaderboard');
  const showEventsCard = canAccessFeatureFromSnapshot(featureAccess, 'events');

  useEffect(() => {
    const unavailable = searchParams.get('unavailable') as FeatureKey | null;
    if (!unavailable) return;

    setUnavailableMessage(featureUnavailableMessage(unavailable));
    const next = new URLSearchParams(searchParams);
    next.delete('unavailable');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    if (!unavailableMessage) return;
    const timer = window.setTimeout(() => setUnavailableMessage(null), 5000);
    return () => window.clearTimeout(timer);
  }, [unavailableMessage]);

  return (
    <div className="space-y-8">
      {unavailableMessage && (
        <p className="text-sm text-[var(--color-text-secondary)] bg-[var(--color-surface-2)] rounded-[var(--radius-md)] px-4 py-3 border border-[var(--color-border)]">
          {unavailableMessage}
        </p>
      )}

      <header>
        <h1 className="text-2xl font-semibold text-[var(--color-text-primary)]">
          Welcome back, {member?.name?.split(' ')[0] || 'Member'}
        </h1>
        <p className="text-[var(--color-text-secondary)] text-sm mt-1">
          Here&apos;s what&apos;s happening in the community.
        </p>
      </header>

      {banner?.visible !== false && (
        <section className="bg-[var(--color-surface-1)] rounded-[var(--radius-xl)] p-6 md:p-8 border border-[var(--color-border)]">
          <h2 className="text-xl font-semibold text-[var(--color-text-primary)] mb-2">
            {banner?.title || 'Mandal Vision 2026'}
          </h2>
          <MarkdownContent
            text={
              banner?.description ||
              'Strengthening our bonds through spiritual, social, and professional growth activities for the Maheshwari community in Chennai.'
            }
            className="text-[var(--color-text-secondary)] text-sm leading-relaxed"
          />
          {hasBannerCountdown(banner) && (
            <BannerCountdown endsAt={banner!.countdownEndsAt!} label={banner?.countdownText} />
          )}
          <a
            className="inline-flex items-center bg-[var(--color-accent)] hover:bg-[var(--color-accent-hover)] text-white font-medium py-2.5 px-5 rounded-[var(--radius-md)] transition-colors duration-[var(--duration-fast)] text-sm mt-4"
            href={banner?.linkUrl || 'https://smymchennai.in/'}
            target="_blank"
            rel="noopener noreferrer"
          >
            {banner?.linkText?.trim() || 'Learn More'}
          </a>
        </section>
      )}

      {campaign && (showQuizCard || showLeaderboardCard) && (
        <section className="bg-[var(--color-accent-subtle)] rounded-[var(--radius-xl)] p-6 md:p-8 border border-[var(--color-border)] space-y-6">
          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
            <div>
              <CampaignSponsorLogo
                sponsorLogoUrl={campaign.sponsorLogoUrl}
                sponsorNames={campaign.sponsorNames}
                size="compact"
                className="mb-4"
              />
              <p className="text-xs font-semibold uppercase tracking-wide text-[var(--color-accent)] mb-1">
                {summary?.phase === 'upcoming' ? 'Upcoming Quiz' : summary?.phase === 'active' ? 'Live Quiz' : 'Quiz Results'}
              </p>
              <h2 className="text-xl font-semibold text-[var(--color-text-primary)]">{campaign.title}</h2>
              {campaign.description ? (
                <MarkdownContent
                  text={campaign.description}
                  className="text-[var(--color-text-secondary)] text-sm mt-2 max-w-2xl"
                />
              ) : null}
              {shouldShowCampaignSponsorBlock(campaign.sponsorNames, campaign.sponsorLinkUrl) && (
                <CampaignSponsorBlock
                  sponsorNames={campaign.sponsorNames}
                  sponsorLinkUrl={campaign.sponsorLinkUrl}
                  align="left"
                  className="mt-2"
                />
              )}
            </div>
            <div className="flex items-center gap-2 text-sm text-[var(--color-text-secondary)]">
              <Users className="w-4 h-4" />
              <span>{summary?.participantCount ?? 0} participants</span>
            </div>
          </div>

          {summary?.currentSession && (
            <p className="text-sm text-[var(--color-text-secondary)]">
              Current round:{' '}
              <span className="font-medium text-[var(--color-text-primary)]">{summary.currentSession.label}</span>
            </p>
          )}
          {summary?.nextSession && (
            <p className="text-sm text-[var(--color-text-secondary)]">
              {summary.currentSession ? 'Upcoming round' : 'Next round'}{' '}
              <span className="font-medium text-[var(--color-text-primary)]">{summary.nextSession.label}</span>
              {' '}opens {formatDateTime(summary.nextSession.opensAt)}.
            </p>
          )}
          {!summary?.currentSession && !summary?.nextSession && summary?.phase !== 'ended' && (
            <p className="text-sm text-[var(--color-text-secondary)]">
              No rounds are scheduled yet for this campaign.
            </p>
          )}

          <div className="flex flex-wrap gap-3">
            {showQuizCard && (
              <Link
                to="/quiz"
                className="inline-flex items-center bg-[var(--color-accent)] hover:bg-[var(--color-accent-hover)] text-white font-medium py-2.5 px-5 rounded-[var(--radius-md)] transition-colors duration-[var(--duration-fast)] text-sm"
              >
                {summary?.currentSession ? 'Play Now' : 'View Quiz'}
              </Link>
            )}
            {showLeaderboardCard && (
              <Link
                to="/leaderboard"
                className="inline-flex items-center bg-[var(--color-surface-1)] border border-[var(--color-border)] hover:bg-[var(--color-surface-2)] text-[var(--color-text-primary)] font-medium py-2.5 px-5 rounded-[var(--radius-md)] transition-colors duration-[var(--duration-fast)] text-sm"
              >
                Full Leaderboard
              </Link>
            )}
          </div>

          {(summary?.top5?.length ?? 0) > 0 && (
            <div className="bg-[var(--color-surface-1)] rounded-[var(--radius-lg)] border border-[var(--color-border)] overflow-hidden">
              <div className="px-4 py-3 border-b border-[var(--color-border)] flex items-center gap-2">
                <Trophy className="w-4 h-4 text-[var(--color-accent)]" />
                <h3 className="text-sm font-semibold text-[var(--color-text-primary)]">Top 5</h3>
              </div>
              <div className="divide-y divide-[var(--color-border)]">
                {summary!.top5.map((entry) => (
                  <div key={entry.uid} className="px-4 py-2.5 flex items-center justify-between gap-3 text-sm">
                    <div className="flex items-center gap-3 min-w-0">
                      <span className={cn(
                        'w-6 h-6 rounded-[var(--radius-sm)] flex items-center justify-center text-xs font-semibold shrink-0',
                        entry.rank === 1 ? 'bg-[var(--color-accent)] text-white' : 'bg-[var(--color-surface-2)] text-[var(--color-text-tertiary)]',
                      )}>
                        {entry.rank}
                      </span>
                      <span className="truncate font-medium text-[var(--color-text-primary)]">{entry.name}</span>
                    </div>
                    <span className="text-[var(--color-accent)] font-semibold tabular-nums shrink-0">{entry.totalPoints} pts</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </section>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {showQuizCard && (
          <Link to="/quiz" className="group">
            <div className="bg-[var(--color-surface-1)] border border-[var(--color-border)] rounded-[var(--radius-lg)] p-6 hover:shadow-[var(--shadow-md)] transition-shadow duration-[var(--duration-normal)] h-full">
              <div className="flex justify-between items-start mb-4">
                <div className="w-10 h-10 bg-[var(--color-accent-muted)] rounded-[var(--radius-md)] flex items-center justify-center text-[var(--color-accent)]">
                  <Trophy className="w-5 h-5" />
                </div>
                <ChevronRight className="w-4 h-4 text-[var(--color-text-tertiary)] group-hover:text-[var(--color-text-secondary)] transition-colors" />
              </div>
              <h3 className="text-base font-semibold text-[var(--color-text-primary)]">Quiz</h3>
              <p className="text-[var(--color-text-secondary)] text-sm mt-1">
                {summary?.nextSession && !summary?.currentSession
                  ? `${summary.nextSession.label} opens ${formatDateTime(summary.nextSession.opensAt)}.`
                  : campaign
                    ? `Join ${campaign.title} rounds and earn points.`
                    : 'Play the current campaign round.'}
              </p>
            </div>
          </Link>
        )}

        {showLeaderboardCard && (
          <Link to="/leaderboard" className="group">
            <div className="bg-[var(--color-surface-1)] border border-[var(--color-border)] rounded-[var(--radius-lg)] p-6 hover:shadow-[var(--shadow-md)] transition-shadow duration-[var(--duration-normal)] h-full">
              <div className="flex justify-between items-start mb-4">
                <div className="w-10 h-10 bg-[var(--color-surface-2)] rounded-[var(--radius-md)] flex items-center justify-center text-[var(--color-text-secondary)]">
                  <Medal className="w-5 h-5" />
                </div>
                <ChevronRight className="w-4 h-4 text-[var(--color-text-tertiary)] group-hover:text-[var(--color-text-secondary)] transition-colors" />
              </div>
              <h3 className="text-base font-semibold text-[var(--color-text-primary)]">Leaderboard</h3>
              <p className="text-[var(--color-text-secondary)] text-sm mt-1">
                {campaign ? `See who leads ${campaign.title}.` : 'Campaign standings and rankings.'}
              </p>
            </div>
          </Link>
        )}

        {showEventsCard && (
          <Link to="/events" className="group md:col-span-2">
            <div className="bg-[var(--color-surface-1)] border border-[var(--color-border)] rounded-[var(--radius-lg)] p-6 hover:shadow-[var(--shadow-md)] transition-shadow duration-[var(--duration-normal)]">
              <div className="flex justify-between items-start mb-4">
                <div className="w-10 h-10 bg-[var(--color-surface-2)] rounded-[var(--radius-md)] flex items-center justify-center text-[var(--color-text-secondary)]">
                  <Calendar className="w-5 h-5" />
                </div>
                <ChevronRight className="w-4 h-4 text-[var(--color-text-tertiary)] group-hover:text-[var(--color-text-secondary)] transition-colors" />
              </div>
              <h3 className="text-base font-semibold text-[var(--color-text-primary)]">Events</h3>
              <p className="text-[var(--color-text-secondary)] text-sm mt-1">
                Community gatherings, activities, and past highlights.
              </p>
            </div>
          </Link>
        )}
      </div>
    </div>
  );
}
